import { useState, useMemo, Fragment } from 'react';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import LanguageIcon from '@mui/icons-material/Language';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SyncIcon from '@mui/icons-material/Sync';
import {
  Box,
  Button,
  Chip,
  MenuItem,
  Select,
  Switch,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Link } from 'react-router-dom';
import ChangeImpactTimeline from '@/components/ChangeImpactTimeline';
import { PageHeader } from '@/components/shared';
import FilterTabs from '@/trio-ui/FilterTabs';
import Pagination from '@/trio-ui/Pagination';
import Table, { Td, Th, Tr } from '@/trio-ui/Table';
import { SeoPage, PageImpactSummary } from '@/types/api/seo';
import * as Styles from './SeoPageList.styles';

type KeywordForPage = {
  id: string;
  keyword: string;
  position: number | null;
};

type Props = {
  pages: SeoPage[];
  keywordsByPage: Map<string, KeywordForPage[]>;
  isLoading: boolean;
  onSyncSitemap: () => void;
  isSyncing: boolean;
  syncCount: number | null;
  syncError: string | null;
  hasSiteUrl: boolean;
  impactByPage: Map<string, PageImpactSummary>;
  kdMap: Map<string, number>;
  projectId: string;
  onToggleAutomation: (pageId: string, enabled: boolean) => Promise<void>;
  onUpdateTrack: (pageId: string, track: 'blog' | 'service' | null) => Promise<void>;
};

const isServicePageUrl = (url: string): boolean => {
  const lower = url.toLowerCase();
  return (
    /\/(hire|find|outsource|engage|staff|book)[-/]/.test(lower) ||
    /developers?\/?$|engineers?\/?$/i.test(lower)
  );
};

const kdColor = (kd: number | null | undefined): string => {
  if (kd == null) return '#96979b';
  if (kd < 30) return '#0D9D58';
  if (kd < 60) return '#F4B400';
  return '#EA4335';
};

const kdLabel = (kd: number | null | undefined): string => {
  if (kd == null) return '';
  if (kd < 30) return 'Easy';
  if (kd < 60) return 'Medium';
  return 'Hard';
};

const formatPath = (url: string): string => {
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
};

const positionColor = (pos: number | null | undefined): string => {
  if (pos == null) return '#96979b';
  if (pos <= 3) return '#0D9D58';
  if (pos <= 10) return '#4285F4';
  if (pos <= 20) return '#F4B400';
  return '#EA4335';
};

const SeoPageListComponent = function ({
  pages,
  keywordsByPage,
  isLoading,
  onSyncSitemap,
  isSyncing,
  syncCount,
  syncError,
  hasSiteUrl,
  impactByPage,
  kdMap,
  projectId,
  onToggleAutomation,
  onUpdateTrack,
}: Props): JSX.Element {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [expandedPageId, setExpandedPageId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('all');

  const basePath = `/seox/projects/${projectId}`;

  const servicePageCount = useMemo(
    () => pages.filter((p) => p.seoTrack === 'service' || isServicePageUrl(p.url)).length,
    [pages]
  );
  const blogPageCount = useMemo(() => pages.filter((p) => p.seoTrack === 'blog').length, [pages]);
  const automationCount = useMemo(() => pages.filter((p) => p.automationEnabled).length, [pages]);

  const filteredPages = useMemo(() => {
    let result = pages;
    if (activeTab === 'service') {
      result = result.filter((p) => p.seoTrack === 'service' || isServicePageUrl(p.url));
    } else if (activeTab === 'blog') {
      result = result.filter((p) => p.seoTrack === 'blog');
    } else if (activeTab === 'automated') {
      result = result.filter((p) => p.automationEnabled);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (p) =>
          p.path.toLowerCase().includes(q) ||
          p.url.toLowerCase().includes(q) ||
          (p.title ?? '').toLowerCase().includes(q)
      );
    }
    return result;
  }, [pages, search, activeTab]);

  const lastPage = Math.max(1, Math.ceil(filteredPages.length / itemsPerPage));
  const paginatedPages = filteredPages.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <Box sx={{}}>
      <PageHeader
        title="Pages"
        actions={
          hasSiteUrl ? (
            <Button
              variant="outlined"
              startIcon={isSyncing ? <CircularProgress size={16} /> : <SyncIcon />}
              onClick={onSyncSitemap}
              disabled={isSyncing}
              sx={{
                borderColor: '#054AD9',
                color: '#054AD9',
                '&:hover': { borderColor: '#043eaf', backgroundColor: '#054AD910' },
                textTransform: 'none',
              }}
            >
              {isSyncing ? 'Syncing...' : 'Sync from Sitemap'}
            </Button>
          ) : undefined
        }
      />

      {syncCount != null && syncCount > 0 && (
        <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#E6F4EA', borderRadius: 1 }}>
          <Typography variant="body2" color="#0D9D58" fontWeight={500}>
            Synced {syncCount} pages from sitemap.
          </Typography>
        </Box>
      )}

      {syncError && (
        <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#FEE2E2', borderRadius: 1 }}>
          <Typography variant="body2" color="#DC2626" fontWeight={500}>
            {syncError}
          </Typography>
        </Box>
      )}

      {!isLoading && pages.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <FilterTabs
            tabs={[
              { key: 'all', label: 'All', count: pages.length },
              { key: 'blog', label: 'Blog', count: blogPageCount },
              { key: 'service', label: 'Service', count: servicePageCount },
              { key: 'automated', label: 'Automated', count: automationCount },
            ]}
            activeKey={activeTab}
            onChange={(key) => {
              setActiveTab(key);
              setCurrentPage(1);
            }}
          />
        </Box>
      )}

      {/* eslint-disable-next-line no-nested-ternary */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#054AD9' }} />
        </Box>
      ) : pages.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <LanguageIcon sx={{ fontSize: 48, color: '#054AD920', mb: 1 }} />
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            No pages found.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {hasSiteUrl
              ? 'Click "Sync from Sitemap" to import pages from your XML sitemap.'
              : 'Set a site URL in project settings first, then sync your sitemap.'}
          </Typography>
          {hasSiteUrl && (
            <Button
              variant="contained"
              startIcon={<SyncIcon />}
              onClick={onSyncSitemap}
              disabled={isSyncing}
              sx={{
                backgroundColor: '#054AD9',
                '&:hover': { backgroundColor: '#043eaf' },
                textTransform: 'none',
              }}
            >
              Sync from Sitemap
            </Button>
          )}
        </Box>
      ) : (
        <>
          {/* Search */}
          <Styles.SearchBox>
            <Styles.SearchIcon>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </Styles.SearchIcon>
            <Styles.SearchInput
              placeholder="Search pages by URL or title..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
            />
          </Styles.SearchBox>

          <Styles.CountLabel>
            {search
              ? `${filteredPages.length} of ${pages.length} pages`
              : `${pages.length} page${pages.length !== 1 ? 's' : ''}`}
          </Styles.CountLabel>

          <Table
            data={paginatedPages}
            renderHeader={
              <Tr>
                <Th>Page</Th>
                <Th>Title</Th>
                <Th textAlign="center">Track</Th>
                <Th>Keywords Targeting</Th>
                <Th textAlign="center">Best Position</Th>
                <Th textAlign="center">Avg KD</Th>
                <Th textAlign="center">Impact</Th>
                <Th textAlign="center">Automation</Th>
              </Tr>
            }
            renderRow={(pg) => {
              const keywords = keywordsByPage.get(pg.url) ?? keywordsByPage.get(pg.id) ?? [];
              const bestPosition =
                keywords.length > 0
                  ? Math.min(...keywords.map((k) => k.position ?? 999).filter((p) => p < 999))
                  : null;
              const impact = impactByPage.get(pg.url);
              const isExpanded = expandedPageId === pg.id;
              // Compute avg KD for keywords targeting this page
              const kwKDs = keywords
                .map((k) => kdMap.get(k.keyword.toLowerCase()))
                .filter((v): v is number => v != null);
              const avgKD =
                kwKDs.length > 0
                  ? Math.round(kwKDs.reduce((s, v) => s + v, 0) / kwKDs.length)
                  : null;

              return (
                <Fragment key={pg.id}>
                  <Tr
                    onClick={() => impact && setExpandedPageId(isExpanded ? null : pg.id)}
                    style={{ cursor: impact ? 'pointer' : 'default' }}
                  >
                    <Td>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Link
                          to={`${basePath}/pages/${pg.id}`}
                          style={{ textDecoration: 'none', color: 'inherit' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            sx={{
                              maxWidth: 280,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              '&:hover': { color: '#054AD9' },
                            }}
                            title={pg.url}
                          >
                            {formatPath(pg.url)}
                          </Typography>
                        </Link>
                        <a
                          href={pg.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <OpenInNewIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                        </a>
                      </Box>
                    </Td>
                    <Td>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          maxWidth: 220,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'block',
                        }}
                        title={pg.title ?? ''}
                      >
                        {pg.title || '—'}
                      </Typography>
                    </Td>
                    <Td textAlign="center">
                      <Select
                        size="small"
                        value={pg.seoTrack ?? ''}
                        displayEmpty
                        onChange={(e) => {
                          e.stopPropagation();
                          const val = e.target.value as string;
                          onUpdateTrack(pg.id, val === '' ? null : (val as 'blog' | 'service'));
                        }}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          fontSize: 11,
                          height: 24,
                          minWidth: 70,
                          '& .MuiSelect-select': { py: 0, px: 1 },
                        }}
                      >
                        <MenuItem value="" sx={{ fontSize: 12 }}>
                          —
                        </MenuItem>
                        <MenuItem value="blog" sx={{ fontSize: 12 }}>
                          Blog
                        </MenuItem>
                        <MenuItem value="service" sx={{ fontSize: 12 }}>
                          Service
                        </MenuItem>
                      </Select>
                    </Td>
                    <Td>
                      {keywords.length > 0 ? (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {keywords.slice(0, 3).map((kw) => (
                            <Chip
                              key={kw.id}
                              label={kw.keyword}
                              size="small"
                              sx={{ fontSize: 10, height: 20, maxWidth: 140 }}
                            />
                          ))}
                          {keywords.length > 3 && (
                            <Chip
                              label={`+${keywords.length - 3}`}
                              size="small"
                              sx={{ fontSize: 10, height: 20 }}
                            />
                          )}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          No keywords
                        </Typography>
                      )}
                    </Td>
                    <Td textAlign="center">
                      {bestPosition != null && bestPosition < 999 ? (
                        <Chip
                          label={`#${bestPosition.toFixed(1)}`}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: 11,
                            color: '#fff',
                            backgroundColor: positionColor(bestPosition),
                            minWidth: 44,
                            borderRadius: '8px',
                          }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </Td>
                    <Td textAlign="center">
                      {avgKD != null ? (
                        <Chip
                          label={`${avgKD} ${kdLabel(avgKD)}`}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: 10,
                            color: '#fff',
                            backgroundColor: kdColor(avgKD),
                            minWidth: 50,
                            borderRadius: '8px',
                          }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </Td>
                    <Td textAlign="center">
                      {impact ? (
                        (() => {
                          const iconMap: Record<string, JSX.Element | undefined> = {
                            improved: <ArrowUpwardIcon sx={{ fontSize: 12 }} />,
                            declined: <ArrowDownwardIcon sx={{ fontSize: 12 }} />,
                            pending: <ScheduleIcon sx={{ fontSize: 12 }} />,
                          };
                          const labelMap: Record<string, string> = {
                            improved:
                              impact.positionDelta != null
                                ? `+${impact.positionDelta.toFixed(1)}`
                                : 'Improved',
                            declined:
                              impact.positionDelta != null
                                ? `${impact.positionDelta.toFixed(1)}`
                                : 'Declined',
                            pending: `${impact.totalChanges} pending`,
                            mixed: 'Mixed',
                          };
                          const colorMap: Record<
                            string,
                            'success' | 'error' | 'warning' | 'default'
                          > = {
                            improved: 'success',
                            declined: 'error',
                            pending: 'warning',
                          };
                          return (
                            <Chip
                              icon={iconMap[impact.impactStatus]}
                              label={labelMap[impact.impactStatus] ?? 'No change'}
                              size="small"
                              color={colorMap[impact.impactStatus] ?? 'default'}
                              sx={{ fontSize: 10, height: 20 }}
                            />
                          );
                        })()
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </Td>
                    <Td textAlign="center">
                      <Switch
                        size="small"
                        checked={pg.automationEnabled}
                        onChange={(e) => {
                          e.stopPropagation();
                          onToggleAutomation(pg.id, e.target.checked);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#054AD9' },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#054AD9',
                          },
                        }}
                      />
                    </Td>
                  </Tr>
                  {/* Expandable detail row */}
                  {impact && isExpanded && (
                    <Tr>
                      <Td colSpan={8} padding="0">
                        <Box sx={{ py: 2, px: 3, backgroundColor: '#F4F4F5' }}>
                          <Typography
                            variant="caption"
                            fontWeight={500}
                            color="text.secondary"
                            sx={{ mb: 1, display: 'block' }}
                          >
                            Applied Changes ({impact.totalChanges})
                          </Typography>
                          <ChangeImpactTimeline changes={impact.changes} compact />
                        </Box>
                      </Td>
                    </Tr>
                  )}
                </Fragment>
              );
            }}
            renderEmptyState={
              <Tr>
                <Td colSpan={8} textAlign="center">
                  No pages match your search.
                </Td>
              </Tr>
            }
          />

          {filteredPages.length > 0 && (
            <Styles.PaginationWrapper>
              <Pagination
                count={filteredPages.length}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                lastPage={lastPage}
                onChangePageHandler={setCurrentPage}
                onChangeRecordsPerPage={(val) => {
                  setItemsPerPage(val);
                  setCurrentPage(1);
                }}
              />
            </Styles.PaginationWrapper>
          )}
        </>
      )}
    </Box>
  );
};

export default SeoPageListComponent;
